<script setup lang="ts">
import type { Option, Schedule, ScheduleWeek } from '~/types'
import { useConfigStore } from '~/store/useConfigStore'

interface Props {
  seasonId: number
}

const props = defineProps<Props>()

const store = useConfigStore()
const { defaultWeekId } = storeToRefs(store)

const model = defineModel<Maybe<number>>()

const params = computed<Schedule.SearchRequest>(() => ({
  include: ['scheduleWeeks'],
  filter: {
    season_id: props.seasonId,
  },
}))

const seasonId = toRef(props, 'seasonId')
const { fetchData, isLoading } = useLoadSchedules(params)

const { data } = await useAsyncData(
  `weeks-of-season-${props.seasonId}`,
  () => fetchData(),
  {
    watch: [seasonId],
  },
)

const weeks = computed<ScheduleWeek.Model[]>(() => data.value?.[0]?.scheduleWeeks ?? [])
const hasWeeks = computed<boolean>(() => Boolean(data.value?.[0]?.scheduleWeeks?.length))

const options = computed<Option[]>(() => {
  return weeks.value.map((week: ScheduleWeek.Model) => ({
    label: `Week ${week.title}`,
    value: week.id,
  })) || []
})

const initWeekId = computed<Maybe<number>>(() => {
  if (!weeks.value.length) {
    return null
  }

  if (model.value) {
    const hasModelWeekId = weeks.value?.map(({ id }) => id).includes(model.value)

    if (hasModelWeekId) {
      return model.value
    }
  }

  const firstWeekId: Maybe<number> = weeks.value?.[0]?.id ?? null

  if (!defaultWeekId.value) {
    return firstWeekId
  }

  const hasCurrentWeekId = weeks.value?.map(({ id }) => id).includes(defaultWeekId.value)
  return hasCurrentWeekId ? defaultWeekId.value : firstWeekId
})

const initModelValue = () => {
  model.value = initWeekId.value
}

onMounted(() => {
  initModelValue()
})

watch(weeks, () => {
  initModelValue()
})
</script>

<template>
  <div
    v-if="hasWeeks"
    class="weeks-dropdown"
  >
    <UILoadingCard
      v-if="isLoading"
      class="weeks-dropdown__loading"
    />
    <UIDropdown
      v-else
      id="weeks-dropdown"
      v-model="model"
      label="Select Week"
      :options
    />
  </div>
</template>

<style lang="scss" scoped>
.weeks-dropdown {
  @apply flex items-center;

  &__loading {
    @apply max-h-11 w-[200px] border border-black/20 rounded-full;

    :deep(.loading-card__icon) {
      @apply scale-75;
    }
  }
}
</style>
